import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Skeleton from '@mui/material/Skeleton';
import FavoriteTwoToneIcon from '@mui/icons-material/FavoriteTwoTone';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { setSelectedMenuItem } from '../../../redux/legacy/reducers/ducks/legacy/OrderDuck';
import useClasses from '../../../hooks/legacy/useClasses';
import styles from './RestaurantMenu.style';
import { setFavoriteRequest } from '../../../redux/legacy/reducers/ducks/legacy/ProfileDuck';

function SkeletonMenu() {
  return (
    <Box>
      <Grid container spacing={{ xs: 0, md: 2 }}>
        <Grid item xs={12} md={3}>
          <Skeleton variant="rounded" height={48} />
        </Grid>
        <Grid item xs={12} md={9}>
          <Skeleton variant="rounded" height={48} />
        </Grid>
      </Grid>

      <Box my={3}>
        <Skeleton height={50} width={200} />
        <Grid container pt={2} spacing={{ xs: 0, md: 2 }}>
          {Array.from({ length: 10 }).map((x) => (
            <Grid item xs={12} md={6} key={`skeleton-${x}`}>
              <Skeleton height={100} variant="rounded" />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
function RestaurantMenu({ isLoadingMenu }) {
  const dispatch = useDispatch();
  const classes = useClasses(styles);
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const { menuItems, categories, type, accessToken, history } = useSelector(
    ({ restaurants, restaurant, order: orderObj, authorization, profile }) => ({
      menuItems: restaurants?.restaurantMenuResponse?.response?.menuItems,
      categories: restaurant?.selectedRestaurantMenu?.categories,
      type: orderObj?.type,
      accessToken: authorization?.accessToken,
      history: profile?.orders,
    })
  );

  const filteredData = useMemo(() => {
    const filteredMenuItems = {};
    Object.values(menuItems || {}).forEach((menuItem) => {
      const isMatch = [menuItem?.name, menuItem?.description].some((text) =>
        text?.toLowerCase().includes(searchText.toLowerCase())
      );
      if (isMatch) {
        filteredMenuItems[menuItem.id] = menuItem;
      }
    });

    const filteredCategories = Object.values(categories || {})
      .filter((category) => {
        const filteredItems = (category?.items || []).filter(
          (item) => filteredMenuItems[item]
        );
        return filteredItems.length;
      })
      .sort((a, b) => a.sort - b.sort)
      .map((category) => ({
        ...category,
        items: [...(category?.items || [])].sort((a, b) => {
          if (filteredMenuItems[a]?.sort < filteredMenuItems[b]?.sort)
            return -1;
          if (filteredMenuItems[a]?.sort > filteredMenuItems[b]?.sort) return 1;
          return 0;
        }),
      }));

    return { filteredMenuItems, filteredCategories };
  }, [menuItems, searchText, categories]);

  const handleTabChange = (event, newValue) => setSelectedCategory(newValue);

  const onFavorite = useCallback(
    (menuItem) => {
      dispatch(
        setFavoriteRequest({
          id: menuItem.id,
          favorite: !menuItem.favorite,
          type: 'menu_item',
        })
      );
    },
    [dispatch]
  );

  const clearSearch = () => setSearchText('');

  const renderMenuItems = (category) => (
    <Grid container spacing={{ xs: 0, sm: 1, md: 2, lg: 3 }}>
      {(category?.items || []).map((itemId) => {
        const menuItem = filteredData.filteredMenuItems[itemId];
        if (!menuItem) return null;

        const price = menuItem.prices[type.value] || 0;
        return (
          <Grid
            key={`menu-card-${menuItem.id}`}
            item
            md={6}
            sx={{ width: '100%', position: 'relative' }}
          >
            <Card
              data-test-id="RestaurantMenuCard"
              elevation={0}
              sx={{
                display: 'flex',
                height: '116px',
                cursor: 'pointer',
                border: '1px solid #EAECF0',
                '&:hover': { borderColor: 'lightGrey' },
              }}
              onClick={() => dispatch(setSelectedMenuItem({ id: menuItem.id }))}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column' }}
                pl={{ xs: 3, md: 3 }}
                pt={{ xs: 2, md: 2 }}
                pr={{ xs: 12, md: 12 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {menuItem.name}
                </Typography>
                {menuItem.description && (
                  <Typography
                    variant="caption"
                    sx={{
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {menuItem.description}
                  </Typography>
                )}
                <Typography variant="body2">{`$${price.toFixed(
                  2
                )}`}</Typography>
              </Box>
            </Card>

            {accessToken && (
              <IconButton
                data-test-id="favorite-icon-button"
                onClick={() => onFavorite(menuItem)}
                sx={{ position: 'absolute', top: 22, right: 10 }}
              >
                {history?.length > 0 &&
                  (menuItem.favorite ? (
                    <FavoriteIcon className={classes.activeFavoriteIcon} />
                  ) : (
                    <FavoriteTwoToneIcon className={classes.favoriteIcon} />
                  ))}
              </IconButton>
            )}
          </Grid>
        );
      })}
    </Grid>
  );

  const renderCategories = () =>
    filteredData.filteredCategories.map((category) => (
      <Box key={category.id}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, pt: 3 }}
          px={{ xs: 2, md: 0 }}
        >
          {category.name}
        </Typography>
        {renderMenuItems(category)}
      </Box>
    ));

  return (
    <Box data-test-id="restaurantMenuContainer" sx={{ mt: 2, height: '100%' }}>
      <Tabs
        value={selectedCategory}
        onChange={handleTabChange}
        data-test-id="category-tab"
        variant="scrollable"
        scrollButtons
        allowScrollButtonsMobile
      >
        <Tab label="All Categories" value="all" />
        <Tab label="Favorites" value="favorites" />
        {filteredData.filteredCategories.map((category) => (
          <Tab key={category.id} label={category.name} value={category.id} />
        ))}
      </Tabs>

      {!isLoadingMenu ? (
        <>
          <Grid
            container
            sx={{
              background: 'white',
              position: 'sticky',
              zIndex: 5,
              padding: '4px 0px',
            }}
          >
            <Grid item xs={12} my={1} px={{ md: 0, xs: 3 }}>
              <Paper
                component="form"
                elevation={0}
                sx={{
                  p: '6px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid lightgrey',
                }}
              >
                <IconButton sx={{ p: '4px' }} data-test-id="input-search-btn">
                  <SearchIcon />
                </IconButton>
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder="Search menu"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  data-test-id="search-menu"
                />
                {searchText && (
                  <IconButton
                    onClick={clearSearch}
                    sx={{ p: '4px' }}
                    data-test-id="input-close-btn"
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Box>
            {selectedCategory === 'all' && renderCategories()}
            {selectedCategory !== 'all' &&
              selectedCategory !== 'favorites' &&
              renderMenuItems(
                filteredData.filteredCategories.find(
                  (c) => c.id === selectedCategory
                )
              )}
            {selectedCategory === 'favorites' && (
              <Grid container spacing={{ xs: 0, sm: 1, md: 2, lg: 3 }}>
                {Object.values(filteredData.filteredMenuItems)
                  .filter((item) => item.favorite)
                  .map((menuItem) => {
                    const price = menuItem.prices[type.value] || 0;
                    return (
                      <Grid
                        key={menuItem.id}
                        item
                        md={6}
                        sx={{ width: '100%', position: 'relative' }}
                      >
                        <Card
                          data-test-id="RestaurantMenuCard"
                          elevation={0}
                          sx={{
                            display: 'flex',
                            height: '116px',
                            cursor: 'pointer',
                            border: '1px solid #EAECF0',
                            '&:hover': { borderColor: 'lightGrey' },
                          }}
                          onClick={() =>
                            dispatch(setSelectedMenuItem({ id: menuItem.id }))
                          }
                        >
                          <Box
                            sx={{ display: 'flex', flexDirection: 'column' }}
                            pl={{ xs: 3, md: 3 }}
                            pt={{ xs: 2, md: 2 }}
                            pr={{ xs: 12, md: 12 }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {menuItem.name}
                            </Typography>
                            <Typography variant="caption">
                              {menuItem.description}
                            </Typography>
                            <Typography variant="body2">{`$${price.toFixed(
                              2
                            )}`}</Typography>
                          </Box>
                        </Card>
                        <IconButton
                          onClick={() => onFavorite(menuItem)}
                          data-test-id="favorite-icon-button"
                          sx={{ position: 'absolute', top: 22, right: 10 }}
                        >
                          {history?.length > 0 &&
                            (menuItem.favorite ? (
                              <FavoriteIcon
                                className={classes.activeFavoriteIcon}
                              />
                            ) : (
                              <FavoriteTwoToneIcon
                                className={classes.favoriteIcon}
                              />
                            ))}
                        </IconButton>
                      </Grid>
                    );
                  })}
              </Grid>
            )}
          </Box>
        </>
      ) : (
        <SkeletonMenu />
      )}
    </Box>
  );
}

RestaurantMenu.propTypes = {
  isLoadingMenu: PropTypes.bool.isRequired,
};

export default RestaurantMenu;
